import { ChangeEvent, useMemo, useState } from 'react';
import { useLocalization, useLocalizationExtension } from '../../../context/Localization/localization';
import { getStateList } from '../../../helpers/StateList';
import { ICityAndState, getCityAndState } from '../../../API/GoMoto';
import { Formik } from 'formik';
import { nameof } from '../../../helpers/nameof';
import { CTAButton } from '../../../controls/ButtonBase';
import css from './WorldPayBillingInformation.module.scss';
import * as Yup from 'yup';
import store from 'store2';
import { Country } from '../../../API/Enums';
import { GMFormTextField, GMFormZipCodeField } from '../../../controls/TextField';
import { GMFormAutocomplete } from '../../../controls/Autocomplete';
import { GMForm } from '../../../controls/Form';

interface WorldPayBillingInformationProps {
    onProceed: (info: BillingInformation) => void;
}

export interface BillingInformation {
    Street: string;
    ZipCode: string;
    City: string;
    State: string;
}

export const WorldPayBillingInformation = ({ onProceed }: WorldPayBillingInformationProps) => {
    const Localization = useLocalization();
    const { formatInvalid, zipCodeInvalid, formatRequired } = useLocalizationExtension();
    const stateList = getStateList(Country.USA);
    const [loadingPostalAutofill, setLoadingPostalAutofill] = useState(false);

    const initialValues = useMemo<BillingInformation>(() => ({
        Street: store.session.get('billingStreet') ?? '',
        ZipCode: store.session.get('billingZip') ?? '',
        City: store.session.get('billingCity') ?? '',
        State: store.session.get('billingState') ?? '',
    }), []);

    return (
        <div className={css.root}>
            <Formik
                initialValues={initialValues}
                onSubmit={(values) => {
                    const newValues: BillingInformation = {
                        Street: values.Street.trim(),
                        ZipCode: values.ZipCode.trim(),
                        City: values.City.trim(),
                        State: values.State.trim(),
                    };

                    store.session.set('billingStreet', newValues.Street);
                    store.session.set('billingZip', newValues.ZipCode);
                    store.session.set('billingCity', newValues.City);
                    store.session.set('billingState', newValues.State);

                    onProceed(newValues);
                }}
                validationSchema={Yup.object()
                    .shape({
                        [nameof<BillingInformation>('Street')]: Yup.string()
                            .required(formatRequired(Localization.Street))
                            .nullable(),
                        [nameof<BillingInformation>('ZipCode')]: Yup.string()
                            .required(formatRequired(Localization.ZipCode))
                            .matches(new RegExp(/^\d{5}$/), zipCodeInvalid)
                            .nullable(),
                        [nameof<BillingInformation>('City')]: Yup.string()
                            .required(formatRequired(Localization.City))
                            .nullable(),
                        [nameof<BillingInformation>('State')]: Yup.string()
                            .oneOf(stateList || [], formatInvalid(Localization.State))
                            .required(formatRequired(Localization.State))
                            .nullable(),
                    })}
            >
                {({ values, setFieldValue }) => (
                    <GMForm
                        submitButton={
                            <CTAButton
                                styleType={'primary'}
                                type={'submit'}
                                title={Localization.ClickToProceedToCardInformation}
                                label={Localization.Proceed}
                                id={Localization.ClickToProceedToCardInformation}
                                ariaLabel={Localization.ClickToProceedToCardInformation}
                            />
                        }
                        hideAccessibilityButton
                        disableOverflow
                    >
                        <div className={css.header}>
                            <h3 className={css.title}>
                                {Localization.BillingAddress}
                            </h3>
                            <div className={css.requiredLabel}>
                                {Localization.DenotesARequiredField}
                            </div>
                        </div>
                        <GMFormTextField
                            name={nameof<BillingInformation>('Street')}
                            id={nameof<BillingInformation>('Street')}
                            title={Localization.Street}
                            label={Localization.Street}
                            ariaLabel={Localization.Street}
                            disabled={loadingPostalAutofill}
                            shiftAfterSpace
                            required
                        />
                        <GMFormZipCodeField
                            name={nameof<BillingInformation>('ZipCode')}
                            id={nameof<BillingInformation>('ZipCode')}
                            title={Localization.ZipCode}
                            label={Localization.ZipCode}
                            ariaLabel={Localization.ZipCode}
                            onChangeCallback={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                if (event.target?.value && event.target.value.length === 5 && !loadingPostalAutofill) {
                                    setLoadingPostalAutofill(true);

                                    getCityAndState(event.target.value)
                                        .then((cityState: ICityAndState) => {
                                            if (cityState.stateOrProvince) {
                                                setFieldValue(nameof<BillingInformation>('State'), cityState.stateOrProvince.toUpperCase());
                                            }

                                            if (cityState.city) {
                                                setFieldValue(nameof<BillingInformation>('City'), cityState.city);
                                            }
                                        })
                                        .finally(() => setLoadingPostalAutofill(false));
                                }
                            }}
                            capsLock
                            required
                        />
                        <GMFormTextField
                            name={nameof<BillingInformation>('City')}
                            id={nameof<BillingInformation>('City')}
                            title={Localization.City}
                            label={Localization.City}
                            ariaLabel={Localization.City}
                            disabled={loadingPostalAutofill}
                            shiftAfterSpace
                            required
                        />
                        <GMFormAutocomplete
                            name={nameof<BillingInformation>('State')}
                            id={nameof<BillingInformation>('State')}
                            title={Localization.State}
                            label={Localization.State + '\u00A0*'}
                            ariaLabel={Localization.State}
                            options={stateList ? stateList : [values.State]}
                            disabled={loadingPostalAutofill}
                            capsLock
                        />
                    </GMForm>
                )}
            </Formik>
        </div>
    );
};